import { index, modelOptions, mongoose, post, prop, PropType, Ref, Severity } from "@typegoose/typegoose";
import { z } from "zod";
import { getZodSchemaFromClass, ObjectIdString, validate } from "../../zod-decorator/index.js";
import { MessagingChannelAddress } from "../commonSubSchemas/messagingChannelAddress.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type { OutboundMessageFieldsWithDefaults, OutboundMessageSchemaTypes } from "../types/outboundMessage.d.js";
import { trackMixed } from "../utils/mixedSchemaSavingUtils.js";
import { type DtoValidatorTypes } from "../validatableClass.js";

const SCHEMA_KEY = "outboundMessages" as const;

export const OUTBOUND_MESSAGE_TYPES = [
    "warningDigest",
    "optInRequest",
    "subscriptionNotice",
    "settingsConfirm",
    "ack",
    "help",
    "orgWelcome",
    "testNotification",
    "legacyWarning",
] as const;

export type OutboundMessageType = (typeof OUTBOUND_MESSAGE_TYPES)[number];

// A missing status means SENT.
export enum OutboundMessageStatus {
    QUEUED = "QUEUED",
    SENDING = "SENDING",
    SENT = "SENT",
    BLOCKED = "BLOCKED",
    FAILED = "FAILED",
}

export enum OutboundConsentOverride {
    COMPLIANCE_REPLY = "COMPLIANCE_REPLY",
}

export enum OptOutNoticePlacement {
    IN_BODY = "IN_BODY",
    FORCE = "FORCE",
    DISABLE = "DISABLE",
}

export enum OutboundBlockedReason {
    OPTED_OUT_GLOBALLY = "OPTED_OUT_GLOBALLY",
    NO_CONTACT = "NO_CONTACT",
    PROVIDER_OPTED_OUT = "PROVIDER_OPTED_OUT",
}

/**
 * One row per outbound message, on every channel — the audit trail and the source of each compose's
 * `since` cursor. The outbound counterpart of `inboundMessages`, which names its endpoint `sender`.
 */
@index({ "recipient.channel": 1, "recipient.address": 1, sentAt: 1 })
@index({ "recipient.channel": 1, "recipient.address": 1, messageType: 1, sentAt: 1 })
@index({ "recipient.channel": 1, "recipient.address": 1, messageType: 1, queuedAt: 1, sentAt: 1 })
@index({ warningRecordIds: 1 })
@index({ "recipient.channel": 1, "recipient.address": 1, includedOptOutNotice: 1, sentAt: 1 })
@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        collection: "outboundMessages" as const satisfies typeof SCHEMA_KEY,
        _id: true,
    },
})
@post<SchemaBase<OutboundMessage>>(...addSchemaKeyHook(SCHEMA_KEY))
export class OutboundMessage extends SchemaBase<OutboundMessage, OutboundMessageFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    /** The endpoint the message was sent to. Keyed by endpoint so the trail survives address churn. */
    @validate({ childClass: MessagingChannelAddress })
    @prop({ type: () => MessagingChannelAddress, required: true })
    public recipient!: MessagingChannelAddress;

    @validate({ zodValidator: z.enum(OUTBOUND_MESSAGE_TYPES) })
    @prop({ type: () => String, required: true })
    public messageType!: OutboundMessageType;

    @validate({ zodValidator: z.coerce.date().optional() })
    @prop({ type: () => Date, required: false })
    public sentAt?: Date;

    @validate({ zodValidator: z.enum(Object.values(OutboundMessageStatus) as [string, ...string[]]).optional() })
    @prop({ type: () => String, required: false, enum: Object.values(OutboundMessageStatus) })
    public status?: OutboundMessageStatus;

    @validate({ zodValidator: z.coerce.date().optional() })
    @prop({ type: () => Date, required: false })
    public queuedAt?: Date;

    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public providerMessageId?: string;

    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public inReplyToProviderMessageId?: string;

    @validate({ zodValidator: z.enum(Object.values(OutboundConsentOverride) as [string, ...string[]]).optional() })
    @prop({ type: () => String, required: false, enum: Object.values(OutboundConsentOverride) })
    public consentOverride?: OutboundConsentOverride;

    @validate({ zodValidator: z.enum(Object.values(OutboundBlockedReason) as [string, ...string[]]).optional() })
    @prop({ type: () => String, required: false, enum: Object.values(OutboundBlockedReason) })
    public blockedReason?: OutboundBlockedReason;

    /** The exact text sent. */
    @validate({ zodValidator: z.string() })
    @prop({ type: () => String, required: true })
    public body!: string;

    /**
     * The Warning version document `_id`s this digest included — the next compose's delta scan
     * excludes these so an already-texted version is never re-texted. These are version doc ids,
     * not SCD `versionId`s (which are monotonic integers per record).
     */
    @validate({ zodValidator: z.array(ObjectIdString).optional() })
    @prop({ type: () => mongoose.Types.ObjectId, required: false, ref: "Warning" }, PropType.ARRAY)
    public warningVersionDocIds?: Ref<unknown>[];

    /**
     * The SCD recordIds of the warning episodes this digest included. Episode-level provenance —
     * backs "which digests covered this episode" lookups via the multikey index.
     * No ref: recordIds are not Warning `_id`s, so these fields cannot be populated.
     */
    @validate({ zodValidator: z.array(ObjectIdString).optional() })
    @prop({ type: () => mongoose.Types.ObjectId, required: false }, PropType.ARRAY)
    public warningRecordIds?: mongoose.Types.ObjectId[];

    /** Per included Warning version document `_id`, the mode it was flagged at. */
    @validate({
        zodValidator: z.record(z.string(), z.enum(["standard", "critical"])).optional(),
    })
    @trackMixed
    @prop({ type: () => mongoose.Schema.Types.Mixed, required: false, allowMixed: Severity.ALLOW })
    public notificationModeByVersionDocId?: Record<string, "standard" | "critical">;

    /**
     * The organization a message was sent on behalf of, for the message types that have one (org
     * welcome, test notification, legacy warning). Absent on digests, which span every organization the
     * number subscribes to.
     * No ref: nothing populates it. It is here so the org survives the job that carried it.
     */
    @validate({ zodValidator: ObjectIdString.optional() })
    @prop({ type: () => mongoose.Types.ObjectId, required: false })
    public organizationId?: mongoose.Types.ObjectId;

    /** The greatest `versionValidFrom` this digest texted; seeds the next compose's scan floor. */
    @validate({ zodValidator: z.coerce.date().optional() })
    @prop({ type: () => Date, required: false })
    public lastIncludedWarningAt?: Date;

    /** Whether this message carried the "reply STOP" opt-out notice. */
    @validate({ zodValidator: z.boolean(), hasDefault: true })
    @prop({ type: () => Boolean, required: true, default: false })
    public includedOptOutNotice: boolean = false;

    /** Whether this message carried a spam-throttle notice. */
    @validate({ zodValidator: z.boolean(), hasDefault: true })
    @prop({ type: () => Boolean, required: true, default: false })
    public includedSpamThrottleNotice: boolean = false;

    static getters = {} as const satisfies Record<string, (self: OutboundMessage) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<OutboundMessageSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<OutboundMessage, OutboundMessageFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

OutboundMessage.dtoValidators = getZodSchemaFromClass(
    OutboundMessage,
) as unknown as DtoValidatorTypes<OutboundMessageSchemaTypes>;
